import { useEffect, useState } from "react";
import { Loader2, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { api, formatApiError, formatIDR } from "../lib/api";

export function PayDialog({ open, onOpenChange, trips, onPaid }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const total = trips.reduce((s, t) => s + t.payout, 0);

  useEffect(() => {
    if (!open) { setFile(null); setNote(""); }
  }, [open]);

  useEffect(() => {
    if (!file || !file.type.startsWith("image/")) return setPreview(null);
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const submit = async () => {
    if (!file) return toast.error("Upload screenshot bukti transfer");
    const fd = new FormData();
    fd.append("trip_ids", trips.map((t) => t.id).join(","));
    fd.append("note", note);
    fd.append("file", file);
    setSaving(true);
    try {
      await api.post("/payments", fd);
      toast.success("Pembayaran disetujui & ditandai Lunas");
      onOpenChange(false);
      onPaid();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="pay-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Approve Pembayaran</DialogTitle>
          <DialogDescription>Lampirkan screenshot transfer bank. Trip terpilih akan ditandai Lunas.</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl bg-slate-950 text-white p-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-400">{trips[0]?.driver_name} · {trips.length} trip</p>
            <p className="font-num font-bold text-2xl text-emerald-400" data-testid="pay-total">{formatIDR(total)}</p>
          </div>
        </div>
        <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 hover:border-emerald-500 bg-slate-50 p-6 cursor-pointer transition-colors text-center">
          {preview ? <img src={preview} alt="preview" className="max-h-56 rounded-lg object-contain" data-testid="pay-proof-preview" /> : <UploadCloud className="h-8 w-8 text-slate-400" />}
          <span className="text-sm font-medium text-slate-700" data-testid="pay-file-name">{file ? file.name : "Pilih screenshot transfer (JPG/PNG/PDF, maks 10MB)"}</span>
          <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="pay-file-input" />
        </label>
        <div className="space-y-1.5">
          <Label>Catatan (opsional)</Label>
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Contoh: Transfer BCA" data-testid="pay-note-input" />
        </div>
        <Button onClick={submit} disabled={saving} className="h-11 bg-emerald-600 hover:bg-emerald-700 font-semibold" data-testid="pay-submit-btn">
          {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Approve & Tandai Lunas
        </Button>
      </DialogContent>
    </Dialog>
  );
}
